import { io } from 'socket.io-client';

let socket = null;
let reconnectTimer = null;

/**
 * Initialize WebSocket connection with JWT auth
 * @param {string} token - JWT token
 * @param {Object} handlers - { onVehicles, onAlerts, onRouteStatus, onGeofenceAlert, onDriverPosition, onConnect, onDisconnect }
 * @returns {Object} socket instance
 */
export function connectWS(token, handlers = {}) {
  if (socket?.connected) return socket;

  // Disconnect previous if exists
  disconnectWS();

  socket = io(window.location.origin, {
    path: '/planner-api/ws',
    auth: { token },
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionDelay: 2000,
    reconnectionDelayMax: 10000,
    reconnectionAttempts: Infinity,
    timeout: 15000,
  });

  socket.on('connect', () => {
    console.log('[WS] Connected:', socket.id);
    if (handlers.onConnect) handlers.onConnect(socket.id);
  });

  socket.on('disconnect', (reason) => {
    console.log('[WS] Disconnected:', reason);
    if (handlers.onDisconnect) handlers.onDisconnect(reason);
  });

  socket.on('connect_error', (err) => {
    console.warn('[WS] Connection error:', err.message);
    // Fallback: if WS fails persistently, handlers should re-enable polling
    if (handlers.onError) handlers.onError(err);
  });

  // ── Data streams ──

  socket.on('vehicles:update', (vehicles) => {
    if (handlers.onVehicles) handlers.onVehicles(vehicles);
  });

  socket.on('alerts:new', (data) => {
    if (handlers.onAlerts) handlers.onAlerts(data);
  });

  socket.on('route:status', (data) => {
    if (handlers.onRouteStatus) handlers.onRouteStatus(data);
  });

  socket.on('geofence:alert', (data) => {
    if (handlers.onGeofenceAlert) handlers.onGeofenceAlert(data);
  });

  socket.on('driver:position', (data) => {
    if (handlers.onDriverPosition) handlers.onDriverPosition(data);
  });

  socket.on('route:dispatched', (data) => {
    if (handlers.onRouteDispatched) handlers.onRouteDispatched(data);
  });

  // Incidente crítico detectado em rota em execução. Emitido pelo
  // monitor server-side a cada 5 min — só novos incidentes (não-vistos)
  // disparam o evento, portanto cada disparo merece atenção do operador.
  socket.on('route:incident', (data) => {
    if (handlers.onRouteIncident) handlers.onRouteIncident(data);
  });

  socket.on('vehicle:simulated', (data) => {
    if (handlers.onVehicleSimulated) handlers.onVehicleSimulated(data);
  });

  // Driver-reported breakdown / incident — admin needs a loud red toast +
  // a sticky alert in the bell so they can re-dispatch the orphaned OTs.
  socket.on('route:aborted', (data) => {
    if (handlers.onRouteAborted) handlers.onRouteAborted(data);
  });

  // Route deviation — driver went off the planned line (detected by the
  // open LiveRouteMonitor) for longer than the threshold. Fan-out so any
  // OTHER operator dashboards open also get the alert.
  socket.on('route:deviation', (data) => {
    if (handlers.onRouteDeviation) handlers.onRouteDeviation(data);
  });

  // Per-OT delivery confirmation — emitted as soon as each OT report is
  // saved (outcome=resolved), so the admin orders list / live panel can
  // tick the OT to "delivered" in real-time without waiting for the
  // whole route to auto-complete.
  socket.on('order:delivered', (data) => {
    if (handlers.onOrderDelivered) handlers.onOrderDelivered(data);
  });
  socket.on('order:arrived', (data) => {
    if (handlers.onOrderArrived) handlers.onOrderArrived(data);
  });
  // Motorista carregou em "Iniciar serviço" após ter chegado ao local.
  // Distingue "no local à espera" de "no local a fazer o serviço" — Fase 1B
  // do start-service. O backend regista serviceStartedAt no relatório.
  socket.on('order:service_started', (data) => {
    if (handlers.onOrderServiceStarted) handlers.onOrderServiceStarted(data);
  });
  socket.on('order:status', (data) => {
    if (handlers.onOrderStatus) handlers.onOrderStatus(data);
  });

  socket.on('simulation:complete', (data) => {
    if (handlers.onSimulationComplete) handlers.onSimulationComplete(data);
  });

  socket.on('simulation:stop', (data) => {
    if (handlers.onSimulationStop) handlers.onSimulationStop(data);
  });

  // Emitted when a mid-route re-plan restarts the simulation with new
  // geometry. Handlers use this to show a toast like "simulação reiniciada".
  socket.on('simulation:replanned', (data) => {
    if (handlers.onSimulationReplanned) handlers.onSimulationReplanned(data);
  });

  // Driver chat — emitted by the messages routes whenever either side
  // POSTs a new message. Handlers refresh the open thread + bump unread badge.
  socket.on('message:new', (data) => {
    if (handlers.onMessageNew) handlers.onMessageNew(data);
  });

  // Fuel milestone (Fase C) — emitido a cada marco de uma OT de combustível
  // (chegada ao terminal, chegada ao PA, descarga, serviço terminado). Os
  // operadores ligados recebem um toast; os responsáveis externos recebem
  // email/SMS do lado do servidor.
  socket.on('fuel:milestone', (data) => {
    if (handlers.onFuelMilestone) handlers.onFuelMilestone(data);
  });

  // Alarme de velocidade GRAVE em tempo real (módulo speed_alarms). O
  // servidor só emite para tenants com o módulo activo.
  socket.on('speed_alarm:severe', (data) => {
    if (handlers.onSpeedAlarmSevere) handlers.onSpeedAlarmSevere(data);
  });

  return socket;
}

/**
 * Disconnect WebSocket
 */
export function disconnectWS() {
  clearTimeout(reconnectTimer);
  if (socket) {
    socket.removeAllListeners();
    socket.disconnect();
    socket = null;
  }
}

/**
 * Get current socket instance
 */
export function getSocket() {
  return socket;
}

/**
 * Check if WS is connected
 */
export function isWSConnected() {
  return socket?.connected || false;
}
